// Compatibility shim: exposes the `next/navigation` API backed by react-router,
// so components migrated from Next.js keep working with only an import-source change.
import { useMemo } from "react"
import {
  useNavigate,
  useLocation,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from "react-router-dom"

export function useRouter() {
  const navigate = useNavigate()
  // Memoized so the returned object is stable across renders (safe in effect deps).
  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => {}, // no-op in an SPA (server components no longer exist)
      prefetch: () => {}, // no-op
    }),
    [navigate],
  )
}

export function usePathname(): string {
  return useLocation().pathname
}

// Next returns a ReadonlyURLSearchParams directly; react-router returns a tuple.
// Return the params object so existing `.get()` / `.toString()` call sites work.
export function useSearchParams(): URLSearchParams {
  return useRRSearchParams()[0]
}

export const useParams = useRRParams

export function redirect(href: string): never {
  window.location.href = href
  throw new Error(`redirect(${href})`)
}

export function notFound(): never {
  throw new Response("Not Found", { status: 404 })
}
